import { object, string } from "yup";
import type { TFunction } from "i18next";

export type BusinessDetailsFormFields = {
  businessName: string;
  businessNumber: string;
  billingEmail?: string;
  phoneNumber?: string;
};

// Used when only the boolean validity result is needed (e.g. gating the confirm
// button from redux state), where translated messages are never read.
const identityT = ((key: string) => key) as TFunction;

/**
 * Shared business-billing-details schema, field names matching what BillingTypeBox
 * registers with react-hook-form (businessName, businessNumber, billingEmail, phoneNumber).
 * Pass the real i18n `t` when wiring this into a form resolver so error messages are
 * translated; omit it for plain boolean checks.
 */
export const getBusinessDetailsSchema = (t: TFunction = identityT) =>
  object({
    // Backend rejects longer values with a 400 - capped here to avoid that round trip.
    businessName: string()
      .trim()
      .required(t("form.fieldRequired") as string)
      .max(200, t("form.maxLength", { max: 200 }) as string),
    // VAT number formats vary by country (letters, digits, dashes, etc.) - accept any text.
    // Backend rejects longer values with a 400 - capped here to avoid that round trip.
    businessNumber: string()
      .trim()
      .required(t("form.fieldRequired") as string)
      .max(36, t("form.maxLength", { max: 36 }) as string),
    billingEmail: string().email(t("form.invalidEmail") as string),
    // Backend requires the phone number to be all digits after the optional leading "+" - no spaces.
    phoneNumber: string().matches(/^\+?[0-9]{1,15}$/, {
      message: t("form.invalidPhone") as string,
      excludeEmptyString: true,
    }),
  });
